import { useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { stackManager } from '../StackManager';

interface UseHotKeyIsolateOptions {
    /** Включен ли барьер (по умолчанию true). Может быть функцией для динамической проверки */
    enabled?: boolean | (() => boolean);
}

/**
 * Создаёт барьер изоляции для ВСЕХ hotkeys.
 *
 * Барьер блокирует все hotkeys, зарегистрированные НИЖЕ него (раньше по времени).
 * Работает как LIFO-стек: при нажатии клавиши система ищет handler с конца стека,
 * и если встречает активный барьер - останавливается, не обрабатывая hotkeys ниже барьера.
 *
 * @example
 * ```tsx
 * // Вариант 1: С явным ID
 * useHotKeyIsolate('modal', { enabled: () => !!modalsStore.activeModalName });
 *
 * // Вариант 2: Только с options (ID генерируется автоматически)
 * useHotKeyIsolate({ enabled: isMenuOpen });
 *
 * // Вариант 3: Без параметров (всегда enabled)
 * useHotKeyIsolate();
 * ```
 *
 * @param isolateIdOrOptions - ID барьера (string) или опции (object). Если не указан, генерируется автоматически.
 * @param maybeOptions - Опции барьера (если первый параметр - ID)
 */
export const useHotKeyIsolate = (
    isolateIdOrOptions?: string | UseHotKeyIsolateOptions,
    maybeOptions?: UseHotKeyIsolateOptions,
): void => {
    // Определяем что передано: ID или options
    const isFirstParamOptions = typeof isolateIdOrOptions === 'object';
    const isolateId = isFirstParamOptions ? undefined : (isolateIdOrOptions as string | undefined);
    const options = isFirstParamOptions ? (isolateIdOrOptions as UseHotKeyIsolateOptions) : maybeOptions;
    const enabled = options?.enabled;
    const isEnabled = typeof enabled === 'function' ? enabled() : enabled ?? true;

    const idRef = useRef(isolateId || uuidv4());
    const wasAddedRef = useRef(false);

    useEffect(() => {
        const id = idRef.current;

        // Если барьер ещё НЕ был добавлен И enabled=true → добавляем
        if (!wasAddedRef.current && isEnabled) {
            wasAddedRef.current = true;
            stackManager.addBarrier(id);
        }

        // Если барьер УЖЕ добавлен и enabled=false → удаляем
        if (wasAddedRef.current && !isEnabled) {
            wasAddedRef.current = false;
            stackManager.removeBarrier(id);
        }
    }, [isEnabled]);

    // Отдельный useEffect для cleanup при размонтировании
    useEffect(() => {
        const id = idRef.current;

        return () => {
            // Удаляем барьер при размонтировании компонента
            if (wasAddedRef.current) {
                stackManager.removeBarrier(id);
                wasAddedRef.current = false;
            }
        };
    }, []); // Пустой deps = только mount/unmount
};
